/**
 * Small inline ▶ button that speaks a voice-coding command sample so
 * the operator hears the exact cadence the regex parser expects.
 *
 * Renders nothing if there's no sample registered for `phrase` — that
 * lets us decorate cheat-sheet items with `<PlayButton phrase={...} />`
 * without having to maintain a parallel list of "which phrases have
 * audio." Add a sample by extending `public/voice-samples/_generate.ps1`
 * + `src/utils/voiceSamples.js`.
 *
 * Founder ask 2026-05-26: "Create a voice coding sample for the users
 * — if you can speak the way you need it they'll understand better."
 */
import { Volume2 } from "lucide-react";
import { hasVoiceSample, playVoiceSample } from "../../utils/voiceSamples";


export default function VoicePlayButton({ phrase, size = "sm", className = "" }) {
  if (!hasVoiceSample(phrase)) return null;

  const dim = size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5";
  const pad = size === "xs" ? "p-0.5" : "p-1";

  const onClick = (e) => {
    // Stop propagation so the button doesn't also trigger the parent
    // chip's collapse/expand (cheat-sheet phrases are sometimes inside
    // <button> rows).
    e.stopPropagation();
    e.preventDefault();
    playVoiceSample(phrase);
  };

  // Filled purple background instead of bare icon — operators said
  // the previous transparent button blended into the chip and they
  // couldn't see it was tappable. Now it's a clear pill: speaker icon
  // on a coloured circle with a hover-darken state.
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Hear "${phrase}"`}
      aria-label={`Play voice sample for ${phrase}`}
      className={`inline-flex items-center justify-center rounded-full ${pad} bg-purple-500/30 border border-purple-400/60 text-purple-100 hover:bg-purple-500/60 hover:text-white active:scale-95 transition-all touch-manipulation shrink-0 ${className}`}
    >
      <Volume2 className={dim} />
    </button>
  );
}
